import React, { useState, useEffect, useMemo } from 'react';
import { complianceStandardsApi, assetsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SlideDrawer } from '../components/ui/slide-drawer';
import {
  Shield, Plus, Loader2, Download, ChevronRight, Edit3, Trash2, CheckCircle2, Globe, X, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';
import StandardsLibraryModal from '../components/compliance/StandardsLibraryModal';
import { Segmented } from '../components/ui/segmented';

const categoryLabels = {
  crane: 'Cranes', truck: 'Trucks', forklift: 'Forklifts', excavator: 'Excavators',
  lifting_gear: 'Lifting Gear', ewp: 'EWP', custom: 'Custom',
};
const catLabel = (c) => categoryLabels[c] || (c ? c.replace(/_/g, ' ') : '');

const splitJurisdiction = (j) => {
  if (!j) return ['—', ''];
  const parts = j.split('·').map(s => s.trim());
  return [parts[0] || '—', parts.slice(1).join(' · ')];
};

// Interval chip tone from interval_months.
const intervalChip = (months, fallback) => {
  if (months === 0 || months == null) {
    return { label: fallback || 'Once', cls: 'text-[#6B6660] bg-[#F6F4EF] border-[#E6E2D9]' };
  }
  if (months <= 3) return { label: `${months} mo`, cls: 'text-[#8A6D00] bg-[#FFF8D6] border-[#EFE0A0]' };
  return { label: `${months} mo`, cls: 'text-[#3D3A35] bg-[#F6F4EF] border-[#DAD5CA]' };
};

export const ComplianceStandardsPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const { isAdmin } = useAuth();
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showLibrary, setShowLibrary] = useState(false);
  const [assetCatCounts, setAssetCatCounts] = useState({});
  const [assets, setAssets] = useState([]);
  // Drawers
  const [detailStd, setDetailStd] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editStd, setEditStd] = useState(null);

  const loadStandards = async () => {
    try {
      const res = await complianceStandardsApi.list();
      setStandards(res.data || []);
    } catch { toast.error('Failed to load standards'); }
    finally { setLoading(false); }
  };

  const loadAssetCounts = async () => {
    try {
      const res = await assetsApi.getAll();
      const data = res.data;
      const arr = Array.isArray(data) ? data : (data?.items || data?.assets || []);
      setAssets(arr);
      const counts = {};
      arr.forEach(a => { const c = a.category || 'other'; counts[c] = (counts[c] || 0) + 1; });
      setAssetCatCounts(counts);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadStandards(); loadAssetCounts(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try { const res = await complianceStandardsApi.seed(); toast.success(res.data.message); loadStandards(); }
    catch { toast.error('Failed to seed standards'); }
    finally { setSeeding(false); }
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Delete this standard?',
      description: 'This compliance standard will be permanently removed.',
      testId: 'confirm-delete-standard',
      onConfirm: async () => {
        try { await complianceStandardsApi.delete(id); toast.success('Standard deleted'); setDetailStd(null); loadStandards(); }
        catch { toast.error('Failed to delete'); }
      },
    });
  };

  const governedCount = (std) => (std.asset_categories || []).reduce((sum, c) => sum + (assetCatCounts[c] || 0), 0);

  // Category chips derived from present standards (no zero-count chips).
  const categoryChips = useMemo(() => {
    const counts = {};
    standards.forEach(s => (s.asset_categories || []).forEach(c => { counts[c] = (counts[c] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => catLabel(a[0]).localeCompare(catLabel(b[0]))).map(([id, count]) => ({ id, count }));
  }, [standards]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return standards.filter(s => {
      const matchesCat = category === 'all' || (s.asset_categories || []).includes(category);
      const matchesSearch = !q ||
        s.code?.toLowerCase().includes(q) ||
        s.name?.toLowerCase().includes(q) ||
        (s.asset_categories || []).some(c => catLabel(c).toLowerCase().includes(q));
      return matchesCat && matchesSearch;
    });
  }, [standards, search, category]);

  const hasFilters = search !== '' || category !== 'all';
  const clearFilters = () => { setSearch(''); setCategory('all'); };

  const openAdd = () => { setEditStd(null); setFormOpen(true); };
  const openEdit = (std) => { setEditStd(std); setDetailStd(null); setFormOpen(true); };

  return (
    <div className="space-y-6" data-testid="compliance-standards-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Compliance Standards
          </h1>
          <p className="text-muted-foreground">Inspection and certification requirements applied by asset category</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowLibrary(true)} data-testid="open-standards-library">
            <Globe className="w-4 h-4 mr-2" /> International library
          </Button>
          {isAdmin() && (
            <Button className="btn-industrial" onClick={openAdd} data-testid="add-standard-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Standard
            </Button>
          )}
        </div>
      </div>

      <StandardsLibraryModal open={showLibrary} onClose={() => setShowLibrary(false)} onImported={() => { loadStandards(); }} />

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : standards.length === 0 ? (
        <Card className="border-[#DAD5CA]">
          <div className="py-14 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Standards Configured</h3>
            <p className="text-muted-foreground text-sm mb-4">Import the default Australian Standards or add your own.</p>
            <Button className="btn-industrial" onClick={handleSeed} disabled={seeding} data-testid="seed-standards-btn">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Import Australian Standards
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden border-[#DAD5CA]">
          {/* Filter row */}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between px-4 py-3 border-b border-[#E6E2D9]">
            <div className="relative w-full lg:max-w-[400px] min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Standard code, name or category"
                className="pl-10 pr-14 h-[42px] border-[1.5px] border-[#1A1A1A] rounded-[9px]" data-testid="compliance-search" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px] font-mono text-[#8F8A7F]">{filtered.length}/{standards.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B6660]">Category</span>
              <Segmented
                value={category}
                onChange={setCategory}
                testId="compliance-category-filter"
                options={[{ id: 'all', label: 'All', count: standards.length, testId: 'compliance-cat-all' },
                  ...categoryChips.map(c => ({ id: c.id, label: catLabel(c.id), count: c.count, testId: `compliance-cat-${c.id}` }))]}
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <div style={{ minWidth: '960px' }}>
              {/* Header */}
              <div className="grid items-center bg-[#F6F4EF] border-b border-[#DAD5CA] h-8 px-[14px] text-[10px] uppercase tracking-[0.1em]"
                style={{ gridTemplateColumns: '120px minmax(220px,2fr) minmax(130px,1fr) minmax(150px,1.1fr) 132px 44px' }}>
                <span className="text-[#1A1A1A] font-bold">Code</span>
                <span className="text-[#6B6660] font-medium">Standard</span>
                <span className="text-[#6B6660] font-medium">Category</span>
                <span className="text-[#6B6660] font-medium">Jurisdiction</span>
                <span className="text-[#6B6660] font-medium">Requirements</span>
                <span />
              </div>
              {/* Rows */}
              {filtered.length === 0 ? (
                <div className="py-14 text-center">
                  <h3 className="font-bold text-[19px]" style={{ fontFamily: 'Manrope, sans-serif' }}>No standards found</h3>
                  <p className="text-muted-foreground text-sm mt-1 mb-4">No standards match your search or category filter.</p>
                  <Button variant="outline" onClick={clearFilters}><X className="w-4 h-4 mr-1" /> Clear filters</Button>
                </div>
              ) : filtered.map(std => {
                const [country, issuer] = splitJurisdiction(std.jurisdiction);
                const nCert = (std.required_certificates || []).length;
                const nInsp = (std.required_inspections || []).length;
                return (
                  <div key={std.id}
                    className="grid items-center px-[14px] py-[11px] border-b border-[#F0EDE6] cursor-pointer transition-colors hover:bg-[#FFFCEF]"
                    style={{ gridTemplateColumns: '120px minmax(220px,2fr) minmax(130px,1fr) minmax(150px,1.1fr) 132px 44px' }}
                    onClick={() => setDetailStd(std)}
                    data-testid={`standard-${std.code}`}
                  >
                    <span className="font-mono text-[12.5px] font-bold text-[#1A1A1A]">{std.code}</span>
                    <span className="text-[13.5px] font-semibold text-[#1A1A1A] truncate pr-3">{std.name}</span>
                    <span>
                      {(std.asset_categories || []).slice(0, 1).map(c => (
                        <span key={c} className="inline-block text-[11px] font-semibold text-[#3D3A35] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[5px] px-2 py-0.5">{catLabel(c)}</span>
                      ))}
                    </span>
                    <span className="pr-3">
                      <span className="block text-[12.5px] text-[#3D3A35]">{country}</span>
                      {issuer && <span className="block text-[10.5px] text-[#8F8A7F]">{issuer}</span>}
                    </span>
                    <span className="font-mono text-[11.5px] text-[#6B6660]">{nCert} cert · {nInsp} insp</span>
                    <span className="text-right"><ChevronRight className="w-[15px] h-[15px] text-[#B5AFA3] inline" /></span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-[14px] py-[11px] bg-[#FBFAF7] border-t border-[#E6E2D9]">
            <div className="flex items-center gap-3">
              {hasFilters && (
                <button onClick={clearFilters} className="inline-flex items-center gap-1 h-[26px] px-2 rounded-md text-[12px] text-[#6B6660] hover:bg-[#F0EDE6]" data-testid="compliance-clear-filters">
                  <X className="w-3.5 h-3.5" /> Clear filters
                </button>
              )}
              <span className="text-[12px] text-[#6B6660]">Showing {filtered.length} of {standards.length} standards</span>
            </div>
            <span className="text-[11.5px] text-[#8F8A7F]">Imported requirements apply to matching assets automatically</span>
          </div>
        </Card>
      )}

      {/* Detail drawer */}
      <SlideDrawer
        open={!!detailStd}
        onOpenChange={(o) => !o && setDetailStd(null)}
        title="Standard"
        titleExtra={detailStd ? <span className="font-mono text-[11px] text-[#8F8A7F] ml-2">{detailStd.code}</span> : null}
        size="lg"
        className="sheet-ground"
        dismissable
      >
        {detailStd && (
          <StandardDetail std={detailStd} isAdmin={isAdmin()} assets={assets} onEdit={() => openEdit(detailStd)} onDelete={() => handleDelete(detailStd.id)} onAssignChanged={loadAssetCounts} />
        )}
      </SlideDrawer>

      {/* Add / Edit drawer */}
      <SlideDrawer
        open={formOpen}
        onOpenChange={(o) => !o && setFormOpen(false)}
        title={editStd ? 'Edit requirements' : 'Add standard'}
        size="lg"
      >
        {formOpen && (
          <StandardForm
            editStd={editStd}
            onBrowseLibrary={() => { setFormOpen(false); setShowLibrary(true); }}
            onCancel={() => setFormOpen(false)}
            onSaved={() => { setFormOpen(false); loadStandards(); }}
          />
        )}
      </SlideDrawer>

      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// ─────────────────────────── Detail drawer body ───────────────────────────
const RequirementCard = ({ title, items, emptyLabel, fallbackKey }) => (
  <div className="rounded-[11px] border border-[#DAD5CA] bg-white overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EFEBE3]">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#6B6660]">{title}</span>
      <span className="font-mono text-[10.5px] text-[#8F8A7F]">{items.length}</span>
    </div>
    {items.length === 0 ? (
      <div className="px-4 py-3 text-[13px] text-[#8F8A7F]">None recorded</div>
    ) : (
      <div>
        {items.map((it, i) => {
          const chip = intervalChip(it.interval_months, fallbackKey ? it[fallbackKey] : undefined);
          return (
            <div key={i} className={`flex items-center gap-2.5 px-4 py-2.5 ${i < items.length - 1 ? 'border-b border-[#F0EDE6]' : ''}`}>
              <CheckCircle2 className="w-[15px] h-[15px] text-[#047857] shrink-0" />
              <span className="text-[13.5px] text-[#1A1A1A] flex-1">{it.type}</span>
              <span className={`text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full border ${chip.cls}`}>{chip.label}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const StandardDetail = ({ std, isAdmin, assets = [], onEdit, onDelete, onAssignChanged }) => {
  const [country, issuer] = splitJurisdiction(std.jurisdiction);
  const certs = std.required_certificates || [];
  const insps = std.required_inspections || [];
  const comps = std.component_types || [];
  const cats = std.asset_categories || [];

  const [linkedSet, setLinkedSet] = useState(() => new Set(assets.filter(a => (a.linked_standards || []).includes(std.id)).map(a => a.id)));
  const [showAssign, setShowAssign] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Assets this standard can apply to: matching category, else all.
  const matching = useMemo(() => {
    const base = cats.length ? assets.filter(a => cats.includes(a.category)) : assets;
    const q = assetSearch.toLowerCase();
    const arr = !q ? base : base.filter(a => `${a.name || ''} ${a.unit_number || ''} ${a.serial_number || ''}`.toLowerCase().includes(q));
    return arr.slice(0, 200);
  }, [assets, cats, assetSearch]);

  const governed = linkedSet.size;
  const unassignedMatching = matching.filter(a => !linkedSet.has(a.id));

  const bulkAssign = async () => {
    if (unassignedMatching.length === 0) return;
    setBusyId('bulk');
    let ok = 0, schedules = 0;
    for (const a of unassignedMatching) {
      try {
        const res = await complianceStandardsApi.linkToAsset(a.id, std.id);
        setLinkedSet(prev => new Set(prev).add(a.id));
        ok += 1; schedules += (res?.data?.schedules_created || 0);
      } catch { /* skip failures, continue */ }
    }
    setBusyId(null);
    toast.success(`Assigned to ${ok} asset${ok === 1 ? '' : 's'}${schedules ? ` · ${schedules} schedule${schedules === 1 ? '' : 's'} created` : ''}`);
    onAssignChanged && onAssignChanged();
  };

  const toggleAssign = async (asset) => {
    const linked = linkedSet.has(asset.id);
    setBusyId(asset.id);
    try {
      if (linked) {
        await complianceStandardsApi.unlinkFromAsset(asset.id, std.id);
        setLinkedSet(prev => { const n = new Set(prev); n.delete(asset.id); return n; });
        toast.success(`Unassigned from ${asset.unit_number || asset.name}`);
      } else {
        const res = await complianceStandardsApi.linkToAsset(asset.id, std.id);
        setLinkedSet(prev => new Set(prev).add(asset.id));
        const n = res?.data?.schedules_created || 0;
        toast.success(`Assigned to ${asset.unit_number || asset.name}${n ? ` · ${n} schedule${n === 1 ? '' : 's'} created` : ''}`);
      }
      onAssignChanged && onAssignChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update assignment');
    } finally { setBusyId(null); }
  };

  return (
    <div className="flex flex-col gap-3.5">
      {/* Title block */}
      <div>
        <h2 className="text-[19px] font-bold text-[#1A1A1A] tracking-[-0.01em] leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="standard-detail-name">{std.name}</h2>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="font-mono text-[12px] font-bold text-[#1A1A1A] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[5px] px-2 py-0.5">{std.code}</span>
          {cats.map(c => (
            <span key={c} className="text-[11px] font-semibold text-[#3D3A35] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[5px] px-2 py-0.5">{catLabel(c)}</span>
          ))}
          <span className="text-[12.5px] text-[#6B6660]">{country}{issuer ? ` · ${issuer}` : ''}</span>
        </div>
        {std.description && <p className="text-[13.5px] leading-[1.55] text-[#3D3A35] mt-2.5" style={{ textWrap: 'pretty' }}>{std.description}</p>}
      </div>

      <RequirementCard title="Required certificates" items={certs} />
      <RequirementCard title="Required inspections" items={insps} fallbackKey="interval" />

      {comps.length > 0 && (
        <div className="rounded-[11px] border border-[#DAD5CA] bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EFEBE3]"><span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#6B6660]">Applies to</span></div>
          <div className="px-4 py-3 flex flex-wrap gap-2">
            {comps.map(ct => (
              <span key={ct} className="text-[12px] text-[#3D3A35] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[6px] px-2.5 py-1">{ct.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      )}

      {/* Assigned assets */}
      <div className="rounded-[11px] border border-[#DAD5CA] bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EFEBE3]">
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#6B6660]">Assigned assets</span>
            <span className="font-mono text-[10.5px] text-[#8F8A7F]">{governed}</span>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowAssign(s => !s)} data-testid="assign-assets-toggle">
              {showAssign ? 'Done' : 'Assign to assets'}
            </Button>
          )}
        </div>
        {!showAssign ? (
          <div className="px-4 py-3 text-[13px] text-[#3D3A35]">
            {governed > 0
              ? <><span className="font-bold text-[#1A1A1A]">{governed}</span> asset{governed === 1 ? '' : 's'} in your fleet are governed by this standard</>
              : <span className="text-[#8F8A7F]">Not yet assigned to any assets. Use “Assign to assets” to activate its requirements and inspection schedules.</span>}
          </div>
        ) : (
          <div>
            <div className="px-4 py-2.5 border-b border-[#F0EDE6] space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={assetSearch} onChange={e => setAssetSearch(e.target.value)} placeholder={cats.length ? `Search ${cats.map(catLabel).join(', ')} assets…` : 'Search assets…'} className="pl-9 h-9" data-testid="assign-asset-search" />
                </div>
                <Button size="sm" className="btn-industrial shrink-0" disabled={busyId === 'bulk' || unassignedMatching.length === 0} onClick={bulkAssign} data-testid="assign-all-btn">
                  {busyId === 'bulk' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  Assign all ({unassignedMatching.length})
                </Button>
              </div>
              {cats.length > 0 && <p className="text-[11px] text-[#8F8A7F]">Showing assets in matching categories: {cats.map(catLabel).join(', ')}</p>}
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {matching.length === 0 ? (
                <div className="px-4 py-6 text-center text-[13px] text-muted-foreground">No matching assets found.</div>
              ) : matching.map((a, i) => {
                const linked = linkedSet.has(a.id);
                return (
                  <div key={a.id} className={`flex items-center gap-3 px-4 py-2.5 ${i < matching.length - 1 ? 'border-b border-[#F0EDE6]' : ''}`} data-testid={`assign-asset-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] text-[#1A1A1A] truncate">
                        {a.unit_number ? <span className="font-mono text-[11px] text-[#C9A400] mr-1.5">{a.unit_number}</span> : null}{a.name}
                      </div>
                      {a.category && <div className="text-[11px] text-[#8F8A7F]">{catLabel(a.category)}</div>}
                    </div>
                    {linked ? (
                      <Button variant="ghost" size="sm" disabled={busyId === a.id} onClick={() => toggleAssign(a)} className="text-[#047857] hover:bg-[#ECFDF5]" data-testid={`assign-unlink-${a.id}`}>
                        {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Assigned</>}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => toggleAssign(a)} data-testid={`assign-link-${a.id}`}>
                        {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-3 pt-3 border-t border-[#DAD5CA] mt-1">
        {isAdmin && (
          <Button className="btn-industrial" onClick={onEdit} data-testid="edit-requirements-btn">
            <Edit3 className="w-4 h-4 mr-2" /> Edit requirements
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" onClick={onDelete} className="ml-auto bg-white border-[#E3B5AF] text-[#B91C1C] hover:bg-[#FEF2F2]" data-testid="delete-standard-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────── Add / Edit form ───────────────────────────
const KindBadge = ({ kind }) => (
  <span className={`inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.09em] px-2 py-1 rounded border ${kind === 'certificate' ? 'text-[#8A6D00] bg-[#FFF8D6] border-[#EFE0A0]' : 'text-[#3D3A35] bg-[#F6F4EF] border-[#DAD5CA]'}`}>
    {kind === 'certificate' ? 'Cert' : 'Insp'}
  </span>
);

const StandardForm = ({ editStd, onBrowseLibrary, onCancel, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState(editStd?.code || '');
  const [name, setName] = useState(editStd?.name || '');
  const [jurisdiction, setJurisdiction] = useState(editStd?.jurisdiction || '');
  const [assetCategory, setAssetCategory] = useState((editStd?.asset_categories || [])[0] || '');
  const [description, setDescription] = useState(editStd?.description || '');
  const [certs, setCerts] = useState((editStd?.required_certificates || []).map(c => ({ type: c.type || '', interval_months: c.interval_months ?? '' })));
  const [insps, setInsps] = useState((editStd?.required_inspections || []).map(c => ({ type: c.type || '', interval_months: c.interval_months ?? '' })));

  const catOptions = ['crane', 'truck', 'forklift', 'excavator', 'lifting_gear', 'ewp', 'custom'];

  const addRow = (setter) => setter(prev => [...prev, { type: '', interval_months: '' }]);
  const removeRow = (setter, idx) => setter(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (setter, idx, field, val) => setter(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const toPayloadRows = (rows) => rows.filter(r => r.type.trim()).map(r => ({
    type: r.type.trim(),
    interval_months: r.interval_months === '' || r.interval_months == null ? null : Number(r.interval_months),
  }));

  const valid = code.trim() && name.trim();

  const submit = async () => {
    if (!valid) { toast.error('Code and name required'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      jurisdiction: jurisdiction.trim(),
      asset_categories: assetCategory ? [assetCategory] : [],
      required_certificates: toPayloadRows(certs),
      required_inspections: toPayloadRows(insps),
    };
    try {
      if (editStd) {
        await complianceStandardsApi.update(editStd.id, payload);
        toast.success('Requirements updated');
      } else {
        await complianceStandardsApi.create({ code: code.trim().toUpperCase(), ...payload });
        toast.success('Standard created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save standard');
    } finally { setSaving(false); }
  };

  const renderRows = (rows, setter, kind, placeholder) => (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: '96px 1fr 120px 34px' }}>
          <KindBadge kind={kind} />
          <Input value={r.type} onChange={e => updateRow(setter, i, 'type', e.target.value)} placeholder={placeholder} className="h-10" data-testid={`req-${kind}-type-${i}`} />
          <Input value={r.interval_months} onChange={e => updateRow(setter, i, 'interval_months', e.target.value.replace(/[^0-9]/g, ''))} placeholder="Months" className="h-10 font-mono" data-testid={`req-${kind}-interval-${i}`} />
          <button type="button" onClick={() => removeRow(setter, i)} className="h-9 w-9 rounded-md flex items-center justify-center text-[#8F8A7F] hover:bg-[#FEF2F2] hover:text-[#B91C1C]" data-testid={`req-${kind}-remove-${i}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      {!editStd && (
        <div className="rounded-[9px] border border-[#DAD5CA] bg-white p-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[13.5px] font-semibold text-[#1A1A1A]">Check the international library first</p>
            <p className="text-[12.5px] text-[#6B6660] mt-0.5">Published standards come pre-filled with their certificates and inspection intervals.</p>
          </div>
          <Button variant="outline" size="sm" onClick={onBrowseLibrary} data-testid="form-browse-library"><Globe className="w-4 h-4 mr-2" />Browse</Button>
        </div>
      )}

      <div className="rounded-[11px] border border-[#DAD5CA] bg-white p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-overline">Code <span className="text-[#B91C1C]">*</span></Label>
            <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. AS 2550" className="h-10" disabled={!!editStd} data-testid="std-code-input" />
          </div>
          <div>
            <Label className="label-overline">Jurisdiction</Label>
            <Input value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} placeholder="e.g. Australia · Standards Australia" className="h-10" />
          </div>
        </div>
        <div>
          <Label className="label-overline">Name <span className="text-[#B91C1C]">*</span></Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Standard name" className="h-10" data-testid="std-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-overline">Asset category</Label>
            <select value={assetCategory} onChange={e => setAssetCategory(e.target.value)} className="h-10 w-full rounded-[8px] border border-[#C9C3B6] px-3 text-[13.5px] bg-white" data-testid="std-category-select">
              <option value="">—</option>
              {catOptions.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-overline">Issuing body</Label>
            <Input value={jurisdiction.includes('·') ? jurisdiction.split('·').slice(1).join('·').trim() : ''} onChange={e => {
              const country = jurisdiction.split('·')[0].trim();
              setJurisdiction(e.target.value ? `${country} · ${e.target.value}` : country);
            }} placeholder="e.g. Standards Australia" className="h-10" />
          </div>
        </div>
        <div>
          <Label className="label-overline">Description</Label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Brief description" />
        </div>
      </div>

      <div className="rounded-[11px] border border-[#DAD5CA] bg-white p-4 space-y-3">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#6B6660]">Requirements</span>
        {renderRows(certs, setCerts, 'certificate', 'Load Test Certificate')}
        {renderRows(insps, setInsps, 'inspection', 'Periodic Inspection')}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => addRow(setCerts)} data-testid="add-cert-req"><Plus className="w-4 h-4 mr-1" /> Certificate</Button>
          <Button variant="outline" size="sm" onClick={() => addRow(setInsps)} data-testid="add-insp-req"><Plus className="w-4 h-4 mr-1" /> Inspection</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-[#DAD5CA]">
        <Button className="btn-industrial" onClick={submit} disabled={saving} data-testid="save-standard-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{editStd ? 'Save requirements' : 'Create standard'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        {!valid && <span className="ml-auto text-[12px] text-[#B91C1C]">Code and name required</span>}
      </div>
    </div>
  );
};

export default ComplianceStandardsPage;
